import { AdmPlan } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DateTimeUtils, NumberUtils, UiUtils } from "@stellar/utils";
import { DateTime } from "luxon";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getErrorDisplayMarkup } from "@components/error-boundary/error-boundary-utils";
import { CREDITS_DISPLAY_NAME } from "@utils/limit-data-display";
import { TEAM_STELLAR_CHANNEL_URL } from "@utils/support-links";
import { capitalize } from "es-toolkit";

/** Label of the plans that grant the tokens a subscription includes */
export const INCLUDED_TOKENS_PLAN_LABEL = `${capitalize(CREDITS_DISPLAY_NAME)} included in a subscription`;

/** The stretch of time a single token plan covers */
export interface TokenPlanPeriod {
  /** The date the token plan starts on */
  startDate: FaroCoreApiTypes.Iso8601Date;

  /** The date the token plan ends on */
  endDate: FaroCoreApiTypes.Iso8601Date;
}

interface TokenPlanPeriodsProps extends TokenPlanPeriod {
  /**
   * Whether the plan never expires. Its end date then lies decades ahead, so splitting it into
   * years would create dozens of token plans, and a single plan covers the whole of it instead.
   */
  isUnlimited: boolean;
}

/**
 * Splits the runtime of a plan into the periods its token plans cover, one for every year the
 * plan runs. The first period starts together with the plan, every later one starts on the
 * anniversary of that date, and the last one ends together with the plan.
 */
export function tokenPlanPeriods({
  startDate,
  endDate,
  isUnlimited,
}: TokenPlanPeriodsProps): TokenPlanPeriod[] {
  const planStart = DateTime.fromISO(startDate, { zone: "utc" });
  const planEnd = DateTime.fromISO(endDate, { zone: "utc" });

  if (isUnlimited || planEnd <= planStart) {
    return [{ startDate, endDate }];
  }

  const periods: TokenPlanPeriod[] = [];

  for (let year = 0; ; year++) {
    const periodStart = planStart.plus({ years: year });
    // A plan that continues another one ends exactly on an anniversary, which starts no new year
    if (periodStart >= planEnd) {
      break;
    }

    // A year of tokens runs right up to its anniversary, so the years follow each other without
    // overlapping and without leaving a gap. Plans end at the end of a day rather than at
    // midnight, so a whole day would be left uncovered if this stepped back a day instead of the
    // smallest possible amount. The last year is cut short to end together with the plan.
    const periodEnd = planStart
      .plus({ years: year + 1 })
      .minus({ milliseconds: 1 });

    // The first year starts and the last year ends together with the plan itself, so both dates
    // are taken over unchanged instead of being derived and formatted again
    periods.push({
      startDate:
        year === 0 ? startDate : (periodStart.toUTC().toISO() ?? startDate),
      endDate:
        periodEnd < planEnd ? (periodEnd.toUTC().toISO() ?? endDate) : endDate,
    });
  }

  return periods;
}

/**
 * Builds the payload of a plan that grants the tokens of a single year.
 *
 * The plan is attached to `plan` and covers `period`, so that the tokens are available for
 * exactly the year they belong to. It is created for the same subject as the plan it belongs to,
 * as the tokens are consumed by the whole workspace and not by an individual member.
 */
export function createTokenPlanPayload({
  plan,
  period,
  tokens,
}: {
  /** The plan the tokens are included in */
  plan: AdmPlan;

  /** The year of the plan the tokens are granted for */
  period: TokenPlanPeriod;

  /** How many tokens the plan grants */
  tokens: number;
}): FaroCoreApiTypes.CreatePlanPayload {
  return {
    subject: {
      type: plan.subject.type,
      id: plan.subject.id,
    },
    subscriberId: plan.subscriber.id,
    label: INCLUDED_TOKENS_PLAN_LABEL,
    featureBundles: [],
    features: [],
    startDate: period.startDate,
    endDate: period.endDate,
    limits: [{ type: "CREDITS", value: tokens }],
    metadata: {
      parentSubscriptionId: plan.id,
    },
  };
}

/** Outcome of creating the token plans that come with a plan */
export interface TokenPlansCreationResult {
  /** Number of token plans that had to be created */
  requestedCount: number;

  /** Number of token plans that could not be created */
  failedCount: number;

  /** The errors of the token plans that could not be created, in the order they were requested */
  errors: unknown[];
}

/**
 * Creates the token plans that come with the given plan, one for every year it runs, and attaches
 * them to it. Nothing is created when the plan includes no tokens.
 *
 * The plans are created independently of each other, so that a single failing request does not
 * prevent the remaining years from being created. Callers are expected to report the failures to
 * the user, as the missing years then have to be added manually.
 */
export async function createTokenPlansForPlan({
  plan,
  tokensPerYear,
  isUnlimited,
}: {
  /** The plan the token plans are attached to */
  plan: AdmPlan;

  /** How many tokens every year of the plan grants */
  tokensPerYear: number;

  /** Whether the plan never expires */
  isUnlimited: boolean;
}): Promise<TokenPlansCreationResult> {
  // A value that is not a finite number would reach the payload as `null`, as that is what
  // JSON.stringify makes of it, so it is turned away here together with the empty allowances
  if (!Number.isFinite(tokensPerYear) || tokensPerYear <= 0 || !plan.endDate) {
    return { requestedCount: 0, failedCount: 0, errors: [] };
  }

  const periods = tokenPlanPeriods({
    startDate: plan.startDate,
    endDate: plan.endDate,
    isUnlimited,
  });

  const results = await Promise.allSettled(
    periods.map(async (period) =>
      faroCoreApiClient.admin.createPlan(
        createTokenPlanPayload({ plan, period, tokens: tokensPerYear })
      )
    )
  );

  const errors = results
    .filter((result) => result.status === "rejected")
    .map((result) => result.reason);

  return {
    requestedCount: periods.length,
    failedCount: errors.length,
    errors,
  };
}

/** Title of the error that is shown when not all token plans of a plan could be created */
export const TOKEN_PLANS_CREATION_FAILED_TITLE = `Not all ${CREDITS_DISPLAY_NAME} could be added`;

/**
 * Builds the body of the error that is shown when not all token plans of a plan could be created.
 *
 * The plan itself already exists at that point, so the missing years have to be added manually.
 * The result is rendered as sanitized HTML by the error dialog, which is what allows it to link
 * to the Teams channel the admin should report the problem in. The sanitizer drops `target`, so
 * the link always opens in the same tab.
 */
export function tokenPlansCreationFailedMarkup({
  requestedCount,
  failedCount,
  errors,
}: TokenPlansCreationResult): string {
  const [firstError] = errors;

  return [
    `<p>${failedCount} of ${requestedCount} years of ${CREDITS_DISPLAY_NAME} could not be added to this plan.</p>`,
    `<p>Please report this immediately in the <a href="${TEAM_STELLAR_CHANNEL_URL}" rel="noopener noreferrer">Team Stellar Teams channel</a>, so that the missing ${CREDITS_DISPLAY_NAME} can be added manually.</p>`,
    firstError === undefined
      ? ""
      : `<p>${getErrorDisplayMarkup(firstError)}</p>`,
  ].join("");
}

/** The stretch of time a token plan covers, as "start - end" */
export function formatTokenPlanPeriod(tokenPlan: AdmPlan): string {
  const startDate = DateTimeUtils.formatDateTimeInUtc({
    date: tokenPlan.startDate,
  });
  const endDate = tokenPlan.endDate
    ? DateTimeUtils.formatDateTimeInUtc({ date: tokenPlan.endDate })
    : UiUtils.DASH;

  return `${startDate} - ${endDate}`;
}

/** How many of the tokens of a token plan are spent already, as "consumed | total" */
export function formatTokenPlanUsage(tokenPlan: AdmPlan): string {
  const credits = tokenPlan.constraints.find(({ type }) => type === "CREDITS");

  if (!credits) {
    return UiUtils.DASH;
  }

  return `${NumberUtils.formatNumberWithSpaces(
    credits.data.current ?? 0
  )} | ${NumberUtils.formatNumberWithSpaces(credits.data.max)}`;
}
